import { Menu, Transition } from "@headlessui/react";
import { ComponentType, Fragment } from "react";

import { Icon } from "@shared/ui";
import { ButtonPrimaryStyle } from "./ButtonPrimary";
import ChevronDown from "../icons/ChevronDown";

type ButtonDropdownMenuItem = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type ButtonDropdownMenuProps = {
  /** Trigger button label. */
  label: string;
  /** Menu items rendered in the dropdown. */
  items?: ButtonDropdownMenuItem[];
  /** Optional icon rendered before the label. */
  leftIcon?: ComponentType<any>;
  /** Accessible label for the trigger (defaults to `label`). */
  ariaLabel?: string;
  /** Extra classes for the menu wrapper. */
  className?: string;
  /** Stretch the trigger (and dropdown) to fill the container. */
  fullWidth?: boolean;
};

/**
 * ButtonDropdownMenu — a primary-styled button that opens a dropdown menu.
 * Visually matches the "Request insurance data" button (see FilefeedImporterTrigger):
 * a black ButtonPrimary trigger with a chevron that rotates when open, over a
 * black dropdown of white menu items. All styling is inline Tailwind so the
 * component is self-contained (no shared CSS classes).
 */
const ButtonDropdownMenu = ({
  label,
  items = [],
  leftIcon,
  ariaLabel,
  className = "",
  fullWidth = false,
}: ButtonDropdownMenuProps) => {
  // Non-fullWidth keeps the trigger content-width (w-fit) so it matches the
  // plain ButtonPrimary submit even inside a stretching flex-column form, and
  // the right-aligned dropdown stays anchored to the button rather than the
  // full column. fullWidth opts into a block CTA that fills its container.
  const wrapperClass = `relative ${
    fullWidth ? "flex w-full" : "inline-flex w-fit"
  } ${className}`.trim();
  const buttonClass = `${ButtonPrimaryStyle}${
    fullWidth ? " w-full justify-between" : ""
  }`;
  // Dropdown panel: fit-content and right-aligned by default; full-width and
  // centered when `fullWidth` so it matches the stretched trigger.
  const itemsClass = `absolute top-full z-20 mt-1 min-w-0 rounded-md bg-black py-1 shadow-lg outline-none ${
    fullWidth
      ? "inset-x-0 w-auto origin-top"
      : "right-0 w-fit origin-top-right"
  }`;

  return (
    <Menu as="div" className={wrapperClass}>
      {({ open }) => (
        <>
          <Menu.Button className={buttonClass} aria-label={ariaLabel || label}>
            <span className="flex items-center gap-x-2">
              {leftIcon && <Icon icon={leftIcon} />}
              {label}
            </span>
            <Icon
              icon={ChevronDown}
              height="h-3"
              width="w-3"
              className={`fill-current transition-transform duration-150 ease-in-out${
                open ? " rotate-180" : ""
              }`}
              aria-hidden="true"
            />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className={itemsClass}>
              {items.map((item, index) => (
                <Menu.Item key={index} disabled={item.disabled}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={item.onClick}
                      disabled={item.disabled}
                      className={`block w-auto whitespace-nowrap px-3 py-2 text-left text-sm text-white ${
                        active ? "bg-gray-900" : "bg-black"
                      }`}
                    >
                      {item.label}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default ButtonDropdownMenu;
